import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, PhoneIncoming, PhoneOutgoing, Mic, Globe, Send,
  User, Mail, MapPin, Flag, Clock, MessageSquare, ChevronDown,
  CheckCircle, AlertCircle, Loader2, Volume2, Settings,
} from 'lucide-react';
import { API as API_BASE } from '../lib/api';

// ── Echo Status Card ──────────────────────────────────────────────────
const StatusCard = ({ label, value, icon: Icon, color = '#D4AF37', sub }) => (
  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 rounded-lg" style={{ background: `${color}18` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

// ── Lead Row ──────────────────────────────────────────────────────────
const LeadRow = ({ lead }) => {
  const priorityColor = lead.priority === 'high' ? '#ef4444' : '#6b7280';
  return (
    <div className="flex items-center gap-4 p-3 rounded-lg bg-white/3 hover:bg-white/5 transition-colors border border-white/5">
      <div className="p-2 rounded-lg" style={{ background: `${priorityColor}18` }}>
        {lead.priority === 'high' ? (
          <Flag className="w-4 h-4 text-red-400" />
        ) : (
          <User className="w-4 h-4 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium text-sm truncate">{lead.name || 'Unknown'}</span>
          {lead.priority === 'high' && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400 uppercase">
              High Priority
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          {lead.phone_display && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone_display}</span>}
          {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
          {lead.locations_count && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.locations_count} locations</span>}
        </div>
      </div>
      <div className="text-right">
        <span className="text-xs text-gray-600">{lead.call_count || 0} calls</span>
        {lead.updated_at && (
          <p className="text-[10px] text-gray-700 mt-0.5">
            {new Date(lead.updated_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Call Row ──────────────────────────────────────────────────────────
const CallRow = ({ call }) => (
  <div className="flex items-center gap-4 p-3 rounded-lg bg-white/3 border border-white/5">
    <div className="p-2 rounded-lg bg-blue-500/10">
      {call.direction === 'inbound' ? (
        <PhoneIncoming className="w-4 h-4 text-blue-400" />
      ) : (
        <PhoneOutgoing className="w-4 h-4 text-emerald-400" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <span className="text-white text-sm font-medium">{call.phone || 'Unknown'}</span>
      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {call.duration_seconds ? `${Math.round(call.duration_seconds)}s` : 'N/A'}
        </span>
        <span>{call.status}</span>
      </div>
    </div>
    <span className="text-xs text-gray-600">
      {call.timestamp ? new Date(call.timestamp).toLocaleString() : ''}
    </span>
  </div>
);

// ── Test Conversation Panel ──────────────────────────────────────────
const TestConversation = ({ token }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `test-${Date.now()}`);

  const sendMessage = async () => {
    if (!message.trim() || loading) return;
    const userMsg = message.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setMessage('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/echo/test-conversation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'echo', text: data.response || data.detail || 'No response.' }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'echo', text: `Error: ${e.message}` }]);
    }
    setLoading(false);
  };

  return (
    <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#D4AF37]/10">
          <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <div>
          <h4 className="text-white font-medium text-sm">Test Echo Conversation</h4>
          <p className="text-xs text-gray-500">Talk to Echo using Gemini 3 Flash</p>
        </div>
      </div>
      <div className="h-64 overflow-y-auto p-4 space-y-3" data-testid="echo-test-chat-messages">
        {messages.length === 0 && (
          <p className="text-center text-gray-600 text-sm py-8">
            Send a message to test Echo's corporate personality...
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] px-4 py-2.5 rounded-xl text-sm ${
                m.role === 'user'
                  ? 'bg-[#D4AF37]/20 text-white border border-[#D4AF37]/30'
                  : 'bg-white/5 text-gray-300 border border-white/10'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
              <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <input
          data-testid="echo-test-chat-input"
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Type a message to Echo..."
          className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-[#D4AF37]/50 focus:outline-none"
        />
        <button
          data-testid="echo-test-chat-send"
          onClick={sendMessage}
          disabled={loading || !message.trim()}
          className="px-4 py-2.5 rounded-lg bg-[#D4AF37] text-[#040F49] font-medium text-sm hover:bg-[#D4AF37]/90 disabled:opacity-40 transition-all"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ── Main Echo Tab ────────────────────────────────────────────────────
const EchoTab = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('overview');

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statusRes, leadsRes, callsRes] = await Promise.all([
        fetch(`${API_BASE}/echo/status`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/echo/leads`, { headers }).then(r => r.json()),
        fetch(`${API_BASE}/echo/calls`, { headers }).then(r => r.json()),
      ]);
      setStatus(statusRes);
      setLeads(leadsRes.leads || []);
      setCalls(callsRes.calls || []);
    } catch (e) {
      console.error('Failed to load Echo data:', e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const SUB_TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'leads', label: `Leads (${leads.length})` },
    { id: 'calls', label: `Call Log (${calls.length})` },
    { id: 'test', label: 'Test Echo' },
  ];

  const vapiReady = status?.vapi_configured;

  return (
    <div className="space-y-6">
      {/* Echo Identity Header */}
      <div className="flex items-center gap-5 p-6 rounded-2xl bg-gradient-to-r from-[#D4AF37]/8 to-blue-600/5 border border-[#D4AF37]/20">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-amber-700 shadow-lg shadow-[#D4AF37]/20">
          <Phone className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white">Supervisor Echo</h2>
          <p className="text-gray-400 text-sm mt-0.5">AI Chief of Staff — Corporate Front Desk</p>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs">
              <Volume2 className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-gray-400">Voice: </span>
              <span className="text-white font-medium">Jessica (ElevenLabs)</span>
            </span>
            <span className="flex items-center gap-1.5 text-xs">
              <Globe className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-gray-400">Languages: </span>
              <span className="text-white font-medium">100+ (Auto-detect)</span>
            </span>
            <span className="flex items-center gap-1.5 text-xs">
              <Mic className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-gray-400">Brain: </span>
              <span className="text-white font-medium">Gemini 3 Flash</span>
            </span>
            {status?.phone?.number && (
              <span className="flex items-center gap-1.5 text-xs">
                <Phone className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-gray-400">Phone: </span>
                <span className="text-white font-medium">{status.phone.number}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {vapiReady ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium">
              <CheckCircle className="w-3.5 h-3.5" /> Live
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-medium">
              <AlertCircle className="w-3.5 h-3.5" /> VAPI Key Needed
            </span>
          )}
        </div>
      </div>

      {/* Sub-tab nav */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/5 border border-white/8 w-fit">
        {SUB_TABS.map(({ id, label }) => (
          <button
            key={id}
            data-testid={`echo-subtab-${id}`}
            onClick={() => setSubTab(id)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: subTab === id ? 'rgba(212,175,55,0.12)' : 'transparent',
              color: subTab === id ? '#D4AF37' : '#9ca3af',
              border: subTab === id ? '1px solid rgba(212,175,55,0.3)' : '1px solid transparent',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {subTab === 'overview' && (
          <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatusCard label="Calls (24h)" value={status?.stats?.calls_24h || 0} icon={PhoneIncoming} color="#3b82f6" />
              <StatusCard label="Total Leads" value={status?.stats?.total_leads || 0} icon={User} color="#D4AF37" />
              <StatusCard label="High Priority" value={status?.stats?.high_priority_leads || 0} icon={Flag} color="#ef4444" />
              <StatusCard
                label="Status"
                value={vapiReady ? 'Live' : 'Setup'}
                icon={vapiReady ? CheckCircle : AlertCircle}
                color={vapiReady ? '#10b981' : '#f59e0b'}
              />
            </div>

            {/* System Config Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Volume2 className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white font-medium text-sm">Voice Engine</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Provider</span><span className="text-white">ElevenLabs</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Voice</span><span className="text-white">Jessica</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Stability</span><span className="text-white">{status?.voice?.stability || 0.85}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Clarity</span><span className="text-white">{status?.voice?.clarity || 0.80}</span></div>
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-blue-400" />
                  <span className="text-white font-medium text-sm">Transcriber</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Provider</span><span className="text-white">Deepgram</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Model</span><span className="text-white">Nova-3</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Language</span><span className="text-white">Multi (100+)</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Auto-detect</span><span className="text-emerald-400">Enabled</span></div>
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-medium text-sm">Conversation AI</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Provider</span><span className="text-white">Gemini</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Model</span><span className="text-white">3 Flash</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Latency</span><span className="text-white">Ultra-low</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Multilingual</span><span className="text-emerald-400">Native</span></div>
                </div>
              </div>
            </div>

            {/* Recent Leads Preview */}
            {leads.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium text-sm">Recent Leads</h3>
                  <button onClick={() => setSubTab('leads')} className="text-xs text-[#D4AF37] hover:underline">
                    View all →
                  </button>
                </div>
                <div className="space-y-2">
                  {leads.slice(0, 3).map((lead, i) => <LeadRow key={i} lead={lead} />)}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {subTab === 'leads' && (
          <motion.div key="leads" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="space-y-2">
              {leads.length === 0 ? (
                <div className="text-center py-16">
                  <User className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No leads captured yet.</p>
                  <p className="text-gray-600 text-xs mt-1">Leads are automatically captured from Echo's phone calls.</p>
                </div>
              ) : (
                leads.map((lead, i) => <LeadRow key={i} lead={lead} />)
              )}
            </div>
          </motion.div>
        )}

        {subTab === 'calls' && (
          <motion.div key="calls" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="space-y-2">
              {calls.length === 0 ? (
                <div className="text-center py-16">
                  <Phone className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No calls recorded yet.</p>
                  <p className="text-gray-600 text-xs mt-1">Call history appears once Echo handles incoming calls via VAPI.</p>
                </div>
              ) : (
                calls.map((call, i) => <CallRow key={i} call={call} />)
              )}
            </div>
          </motion.div>
        )}

        {subTab === 'test' && (
          <motion.div key="test" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TestConversation token={token} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EchoTab;
